// Per-book offline cache backed by IndexedDB. Mirrors the desktop app's
// offline book save (which caches /api/v1/books/:id and
// /api/v1/books/:id/chunks JSON on disk) and the mobile app's network-first
// cachedFetch: callers fetch from the network first, write successful
// responses here, and fall back to these records when the network is
// unavailable.
//
// IndexedDB is client-only — every entry point resolves to a no-op result
// when running server-side (SSR) or in browsers without IndexedDB, so this
// module is safe to import from client components rendered on the server.
//
// Narration audio is NOT cached yet (the desktop app downloads it per chunk;
// on web it would go through the Cache Storage API against the authenticated
// audio routes). Tracked as a follow-up.

import type { BookChunkSummary, BookDetailResponse } from "@/lib/lumi-api";
import type { Chapter } from "@/components/lumi-rust/reader/types";

const DB_NAME = "lumi-offline";
const DB_VERSION = 1;
const BOOKS_STORE = "books";

export interface OfflineBookRecord {
  bookId: string;
  book: BookDetailResponse;
  chunks: BookChunkSummary[];
  /** Chapters power the reader's table of contents offline. Empty when the
   *  book has none (or they failed to load at save time). */
  chapters: Chapter[];
  /** ISO timestamp of the last successful write (save or refresh). */
  savedAt: string;
}

export function isOfflineCacheSupported(): boolean {
  return typeof window !== "undefined" && "indexedDB" in window;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(BOOKS_STORE)) {
        db.createObjectStore(BOOKS_STORE, { keyPath: "bookId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Could not open the offline cache"));
  });
}

/** Run `operate` inside a transaction on the books store, closing the
 *  database when the transaction settles. */
async function withBooksStore<T>(
  mode: IDBTransactionMode,
  operate: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(BOOKS_STORE, mode);
    const request = operate(tx.objectStore(BOOKS_STORE));
    tx.oncomplete = () => {
      db.close();
      resolve(request.result);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error ?? new Error("Offline cache transaction failed"));
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error ?? new Error("Offline cache transaction aborted"));
    };
  });
}

/** The cached record for a book, or null when absent/unsupported. */
export async function readOfflineBook(
  bookId: string,
): Promise<OfflineBookRecord | null> {
  if (!isOfflineCacheSupported()) return null;
  const record = await withBooksStore("readonly", (store) =>
    store.get(bookId),
  );
  return (record as OfflineBookRecord | undefined) ?? null;
}

export async function hasOfflineBook(bookId: string): Promise<boolean> {
  if (!isOfflineCacheSupported()) return false;
  const count = await withBooksStore("readonly", (store) =>
    store.count(bookId),
  );
  return count > 0;
}

/** Save (or fully replace) a book's offline copy. */
export async function writeOfflineBook(input: {
  bookId: string;
  book: BookDetailResponse;
  chunks: BookChunkSummary[];
  chapters?: Chapter[];
}): Promise<void> {
  if (!isOfflineCacheSupported()) return;
  const record: OfflineBookRecord = {
    bookId: input.bookId,
    book: input.book,
    chunks: input.chunks,
    chapters: input.chapters ?? [],
    savedAt: new Date().toISOString(),
  };
  await withBooksStore("readwrite", (store) => store.put(record));
}

/** Network-first write-through: refresh the cached copy with freshly fetched
 *  data, but only for books the user already saved offline (so merely opening
 *  a book never grows the cache). Chapters are preserved unless provided. */
export async function refreshOfflineBook(input: {
  bookId: string;
  book: BookDetailResponse;
  chunks: BookChunkSummary[];
  chapters?: Chapter[];
}): Promise<void> {
  if (!isOfflineCacheSupported()) return;
  const existing = await readOfflineBook(input.bookId);
  if (!existing) return;
  await writeOfflineBook({
    ...input,
    chapters: input.chapters ?? existing.chapters,
  });
}

/** Drop a book's offline copy (e.g. when the book is deleted). */
export async function deleteOfflineBook(bookId: string): Promise<void> {
  if (!isOfflineCacheSupported()) return;
  await withBooksStore("readwrite", (store) => store.delete(bookId));
}
